import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import { handleTokenAndPostRequest } from "../utils/utils";
import { useViewScope } from "../contexts/ViewScopeContext";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { FaEnvelope, FaEnvelopeOpen, FaMousePointer, FaInfoCircle, FaSearch, FaFileAlt, FaNewspaper, FaStickyNote, FaHome, FaRocket, FaCloudUploadAlt, FaQrcode, FaChartArea, FaSms } from "react-icons/fa";
import EmptyFeatureState from "../components/EmptyFeatureState";
import UploadContactsModal from "../components/UploadContactsModal";

const PLACEHOLDER_MU_CHART = [
  { day: "Mon", Delivered: 120, Opened: 82, Clicked: 34 },
  { day: "Tue", Delivered: 145, Opened: 98, Clicked: 41 },
  { day: "Wed", Delivered: 132, Opened: 91, Clicked: 38 },
  { day: "Thu", Delivered: 168, Opened: 115, Clicked: 52 },
  { day: "Fri", Delivered: 155, Opened: 107, Clicked: 48 },
  { day: "Sat", Delivered: 90, Opened: 61, Clicked: 22 },
  { day: "Sun", Delivered: 78, Opened: 52, Clicked: 18 },
];
const PLACEHOLDER_PU_CHART = [
  { day: "Mon", Delivered: 95, Opened: 68, Clicked: 28 },
  { day: "Tue", Delivered: 110, Opened: 79, Clicked: 35 },
  { day: "Wed", Delivered: 102, Opened: 74, Clicked: 31 },
  { day: "Thu", Delivered: 138, Opened: 96, Clicked: 44 },
  { day: "Fri", Delivered: 125, Opened: 88, Clicked: 40 },
  { day: "Sat", Delivered: 72, Opened: 48, Clicked: 16 },
  { day: "Sun", Delivered: 65, Opened: 42, Clicked: 14 },
];

const PERIODS = [
  { label: "7d", value: 7 },
  { label: "30d", value: 30 },
  { label: "60d", value: 60 },
  { label: "90d", value: 90 },
];

const EVENT_COLORS = {
  property_analysed: "#2563eb",
  send_property_report: "#7c3aed",
  get_article: "#059669",
  click_save_user_note: "#d97706",
  view_appraisal: "#dc2626",
};

const EVENT_LABELS = {
  property_analysed: "Property Analysed",
  send_property_report: "Report Sent",
  get_article: "Article View",
  click_save_user_note: "Note Saved",
  view_appraisal: "Appraisal View",
};

function InfoTooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: 4 }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <FaInfoCircle size={12} color="#9ca3af" style={{ cursor: "help" }} />
      {show && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
          background: "#1f2937", color: "#fff", fontSize: 11, fontWeight: 500, lineHeight: 1.4,
          padding: "8px 12px", borderRadius: 6, whiteSpace: "normal", width: 220, zIndex: 20,
          boxShadow: "0 2px 8px rgba(0,0,0,0.18)", pointerEvents: "none",
        }}>
          {text}
        </div>
      )}
    </span>
  );
}

function StatCard({ icon, label, value, info, loading, isMobile, trackField }) {
  return (
    <div
      data-track-card="true"
      data-event-tab="dashboard"
      data-event-field={trackField || 'stat_card'}
      data-event-action="click"
      style={{
      flex: 1,
      minWidth: isMobile ? 0 : 180,
      background: "#fff",
      borderRadius: 16,
      border: "1px solid #e5e7eb",
      padding: isMobile ? "18px 16px" : "22px 24px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
      display: "flex",
      alignItems: "center",
      gap: 12,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: "#f3f4f6",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        color: "#6b7280",
        fontSize: 15,
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        {loading ? (
          <Spinner animation="border" size="sm" style={{ color: "#9ca3af" }} />
        ) : (
          <div style={{
            fontSize: isMobile ? 20 : 24, fontWeight: 700, color: "#1f2937",
            lineHeight: 1.1,
          }}>
            {typeof value === "number" ? new Intl.NumberFormat().format(value) : value}
          </div>
        )}
        <div style={{
          fontSize: 11, fontWeight: 500, color: "#9ca3af",
          marginTop: 2, display: "flex", alignItems: "center",
        }}>
          {label}
          {info && <InfoTooltip text={info} />}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children, isMobile, height = 320 }) {
  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      border: "1px solid #e5e7eb",
      padding: isMobile ? "16px 12px" : "24px 28px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    }}>
      <h3 style={{
        fontSize: 15, fontWeight: 700, color: "#1f2937",
        marginBottom: 20, marginTop: 0,
      }}>
        {title}
      </h3>
      <div style={{ width: "100%", height }}>
        {children}
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", borderRadius: 10, padding: "12px 16px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.12)", border: "1px solid #e5e7eb",
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 6 }}>
        {label}
      </div>
      {payload.filter(p => p.value > 0).map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
          <span style={{ color: "#374151" }}>{p.name}:</span>
          <span style={{ fontWeight: 700, color: "#1f2937" }}>{new Intl.NumberFormat().format(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function EmailAreaChart({ data, idPrefix, isMobile }) {
  if (!data.length) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#9ca3af", fontSize: 14 }}>
        No email data for this period
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <defs>
          <linearGradient id={`${idPrefix}Del`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
            <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
          </linearGradient>
          <linearGradient id={`${idPrefix}Open`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
            <stop offset="95%" stopColor="#059669" stopOpacity={0} />
          </linearGradient>
          <linearGradient id={`${idPrefix}Click`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#d97706" stopOpacity={0.15} />
            <stop offset="95%" stopColor="#d97706" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
        <XAxis dataKey="day" tickFormatter={formatDay} fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
        <YAxis fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={false} tickLine={false} />
        <Tooltip content={<CustomTooltip />} />
        <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "#6b7280" }} />
        <Area type="monotone" dataKey="Delivered" name="Delivered" stroke="#2563eb" fill={`url(#${idPrefix}Del)`} strokeWidth={2} dot={false} />
        <Area type="monotone" dataKey="Opened" name="Opened" stroke="#059669" fill={`url(#${idPrefix}Open)`} strokeWidth={2} dot={false} />
        <Area type="monotone" dataKey="Clicked" name="Clicked" stroke="#d97706" fill={`url(#${idPrefix}Click)`} strokeWidth={2} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function formatDay(day) {
  if (!day) return "";
  const d = new Date(day + "T00:00:00");
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

export default function Dashboard() {
  const { viewScope, agentFilter } = useViewScope();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [error, setError] = useState("");
  const [days, setDays] = useState(90);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [ago, setAgo] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      setError("");
      try {
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/dashboard-stats`,
          { view_scope: viewScope, agent_filter: agentFilter, days }
        );
        setData(res.data);
        setLastUpdated(new Date());
      } catch (e) {
        setError("Failed to load dashboard stats.");
        setData(null);
      }
      setLoading(false);
    }
    fetchStats();
  }, [viewScope, agentFilter, days]);

  // Update "ago" label every 10s
  useEffect(() => {
    function tick() {
      if (!lastUpdated) { setAgo(""); return; }
      const s = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
      if (s < 5) setAgo("just now");
      else if (s < 60) setAgo(`${s}s ago`);
      else setAgo(`${Math.floor(s / 60)}m ago`);
    }
    tick();
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
  }, [lastUpdated]);

  // Build chart data for each campaign type
  const buildEmailChart = (campaignKey) => {
    if (!data?.daily_email) return [];
    const byDay = {};
    for (const row of data.daily_email) {
      if (row.campaign !== campaignKey) continue;
      if (!byDay[row.day]) byDay[row.day] = { day: row.day, Delivered: 0, Opened: 0, Clicked: 0 };
      if (row.event_type === "Delivery") byDay[row.day].Delivered += row.count;
      else if (row.event_type === "Open") byDay[row.day].Opened += row.count;
      else if (row.event_type === "Click") byDay[row.day].Clicked += row.count;
    }
    return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
  };

  const muChart = useMemo(() => buildEmailChart("market_update"), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const puChart = useMemo(() => buildEmailChart("property_update"), [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // SMS sent per day, with one series per campaign
  const smsChart = useMemo(() => {
    if (!data?.daily_sms) return [];
    const byDay = {};
    for (const row of data.daily_sms) {
      if (!byDay[row.day]) byDay[row.day] = { day: row.day, "Market Update": 0, "Property Update": 0 };
      if (row.campaign === "market_update") byDay[row.day]["Market Update"] += row.count;
      else if (row.campaign === "property_update") byDay[row.day]["Property Update"] += row.count;
    }
    return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
  }, [data]);

  // Stacked bar chart for platform events
  const EVENT_KEYS = Object.keys(EVENT_LABELS);
  const eventsChart = useMemo(() => {
    if (!data?.daily_events) return [];
    const byDay = {};
    const emptyDay = () => {
      const o = {};
      for (const k of EVENT_KEYS) o[k] = 0;
      return o;
    };
    for (const row of data.daily_events) {
      if (!byDay[row.day]) byDay[row.day] = { day: row.day, ...emptyDay() };
      byDay[row.day][row.event_name] = (byDay[row.day][row.event_name] || 0) + row.count;
    }
    return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const big = data?.big_numbers || {};

  // Detect new agent (no meaningful activity yet)
  const totalActivity = Object.values(big).reduce((sum, v) => sum + (v || 0), 0);
  const isNewAgent = !loading && data && totalActivity === 0;

  const eventCards = [
    { icon: <FaSearch />, label: "Analysed", value: big.property_analysed || 0, trackField: 'analysed', info: "Number of times a user ran a property analysis on your branded page" },
    { icon: <FaFileAlt />, label: "Reports Sent", value: big.send_property_report || 0, trackField: 'reports_sent', info: "Property reports generated and emailed to users from your branded page" },
    { icon: <FaNewspaper />, label: "Article Views", value: big.article_views || 0, trackField: 'article_views', info: "Number of times users viewed one of your published articles" },
    { icon: <FaStickyNote />, label: "Notes Saved", value: big.notes_saved || 0, trackField: 'notes_saved', info: "Times a user saved personal notes on a property through your branded page" },
    { icon: <FaHome />, label: "Appraisal Views", value: big.appraisal_views || 0, trackField: 'appraisal_views', info: "Number of times users viewed an appraisal you created" },
  ];
  const muCards = [
    { icon: <FaEnvelope />, label: "MU Sent", value: big.mu_sent || 0, trackField: 'mu_sent', info: "Market Update emails delivered — suburb-level market insights sent to contacts who have a preferred suburb but no property address" },
    { icon: <FaEnvelopeOpen />, label: "MU Opens", value: big.mu_opens || 0, trackField: 'mu_opens', info: "How many of the Market Update emails were opened by the recipient" },
    { icon: <FaMousePointer />, label: "MU Clicks", value: big.mu_clicks || 0, trackField: 'mu_clicks', info: "Number of links clicked inside Market Update emails, such as viewing the full report" },
  ];
  const puCards = [
    { icon: <FaEnvelope />, label: "PU Sent", value: big.pu_sent || 0, trackField: 'pu_sent', info: "Property Update emails delivered — personalised updates sent to contacts who have a specific property address on file" },
    { icon: <FaEnvelopeOpen />, label: "PU Opens", value: big.pu_opens || 0, trackField: 'pu_opens', info: "How many of the Property Update emails were opened by the recipient" },
    { icon: <FaMousePointer />, label: "PU Clicks", value: big.pu_clicks || 0, trackField: 'pu_clicks', info: "Number of links clicked inside Property Update emails, such as viewing the property report" },
  ];

  return (
    <>
    <div style={{
      padding: isMobile ? "0 16px" : "0 32px",
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      background: "#f6f8fa",
      boxSizing: "border-box",
      width: "100%",
      position: "relative",
      overflow: "auto",
    }}>
      <div style={{ maxWidth: 1100, marginLeft: "auto", marginRight: "auto", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Sticky header */}
        <div style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "#f6f8fa",
          paddingTop: isMobile ? 60 : 32,
          paddingBottom: 8,
          marginLeft: isMobile ? -16 : -32,
          marginRight: isMobile ? -16 : -32,
          paddingLeft: isMobile ? 16 : 32,
          paddingRight: isMobile ? 16 : 32,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <h1 style={{ fontSize: isMobile ? 22 : 32, fontWeight: 700, margin: "0 0 4px 0", color: "#1f2937" }}>Dashboard</h1>
            {!loading && lastUpdated && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
                <span className="pulse-dot" />
                <span>Live &middot; updated {ago}</span>
              </div>
            )}
          </div>
          {!isMobile && (
            <p style={{ color: "#6b7280", fontSize: 15, marginBottom: 20, lineHeight: 1.6 }}>
              Performance overview across email campaigns, property leads, and platform engagement.
            </p>
          )}

          {/* Period filter */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: isMobile ? 12 : 24, flexWrap: "wrap" }}>
            <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid #e5e7eb" }}>
              {PERIODS.map((p, idx) => (
                <button
                  key={p.value}
                  onClick={() => setDays(p.value)}
                  style={{
                    padding: "8px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    border: "none",
                    borderRight: idx !== PERIODS.length - 1 ? "1px solid #e5e7eb" : "none",
                    background: days === p.value ? "#1f2937" : "#fff",
                    color: days === p.value ? "#fff" : "#6b7280",
                    whiteSpace: "nowrap",
                    transition: "background 0.15s, color 0.15s",
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {isNewAgent && (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <EmptyFeatureState
            icon={<FaRocket />}
            title="Welcome to Your Dashboard"
            description="Once your contacts start engaging — opening emails, viewing reports, and analysing properties — your stats and charts will come to life here."
            features={[
              { icon: <FaCloudUploadAlt />, text: "Upload your contacts to start sending automated market and property updates" },
              { icon: <FaQrcode />, text: "Share your branded property search page via QR code or link" },
              { icon: <FaChartArea />, text: "Track email opens, clicks, property analyses, and article views in real time" },
            ]}
            examplePreview={
              <div style={{ padding: 16 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                  {[
                    { label: "Emails Sent", val: "1,247" },
                    { label: "Opens", val: "843" },
                    { label: "Clicks", val: "312" },
                  ].map(s => (
                    <div key={s.label} style={{
                      background: "#f8fafc", borderRadius: 10, padding: "14px 12px",
                      border: "1px solid #e5e7eb",
                    }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#1f2937" }}>{s.val}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{
                  background: "#f8fafc", borderRadius: 10, padding: 16,
                  border: "1px solid #e5e7eb", height: 80,
                  display: "flex", alignItems: "flex-end", gap: 4,
                }}>
                  {[30, 45, 35, 60, 50, 70, 55, 80, 65, 90, 75, 85].map((h, i) => (
                    <div key={i} style={{
                      flex: 1, height: `${h}%`, borderRadius: 3,
                      background: `linear-gradient(180deg, #2563eb ${100 - h}%, #93c5fd 100%)`,
                      opacity: 0.7,
                    }} />
                  ))}
                </div>
              </div>
            }
            ctaLabel="Get Started"
            onCta={() => navigate("/getting-started")}
            isMobile={isMobile}
          />
          </div>
        )}

        {error ? (
          <div style={{
            background: "#f8fafc",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 2px 8px #0001",
            border: "1px solid #e5e7eb",
            textAlign: "center",
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#1f2937" }}>Unable to Load Dashboard</h2>
            <p style={{ fontSize: 15, color: "#6b7280", marginBottom: 0 }}>{error}</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 40 }}>

            {/* Platform Events — top row */}
            <div>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10, marginTop: 0 }}>Platform Events</h3>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr 1fr", gap: isMobile ? 10 : 16 }}>
                {eventCards.map(c => (
                  <StatCard key={c.label} icon={c.icon} label={c.label} value={c.value} info={c.info} loading={loading} isMobile={isMobile} trackField={c.trackField} />
                ))}
              </div>
            </div>

            {/* Market Update stats — 3 per row */}
            <div>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10, marginTop: 0 }}>Market Updates</h3>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: isMobile ? 10 : 16 }}>
                {muCards.map(c => (
                  <StatCard key={c.label} icon={c.icon} label={c.label} value={c.value} info={c.info} loading={loading} isMobile={isMobile} trackField={c.trackField} />
                ))}
              </div>
            </div>

            {/* Property Update stats — 3 per row */}
            <div>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10, marginTop: 0 }}>Property Updates</h3>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: isMobile ? 10 : 16 }}>
                {puCards.map(c => (
                  <StatCard key={c.label} icon={c.icon} label={c.label} value={c.value} info={c.info} loading={loading} isMobile={isMobile} trackField={c.trackField} />
                ))}
              </div>
            </div>

            {/* Market Update Email Activity */}
            {loading ? (
              <ChartCard title="Market Update — Email Activity" isMobile={isMobile} height={isMobile ? 240 : 300}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                  <Spinner animation="border" />
                </div>
              </ChartCard>
            ) : muChart.length > 0 ? (
              <ChartCard title="Market Update — Email Activity" isMobile={isMobile} height={isMobile ? 240 : 300}>
                <EmailAreaChart data={muChart} idPrefix="mu" isMobile={isMobile} />
              </ChartCard>
            ) : (
              <div style={{
                background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)", overflow: "hidden",
              }}>
                <div style={{ padding: isMobile ? "20px 16px 16px" : "24px 28px 16px" }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", margin: 0 }}>Market Update — Email Activity</h3>
                </div>
                <div style={{ position: "relative", height: isMobile ? 200 : 260, padding: "0 12px" }}>
                  <div style={{ opacity: 0.35, filter: "blur(1px)", height: "100%", pointerEvents: "none" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={PLACEHOLDER_MU_CHART} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="phMuDel" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="phMuOpen" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="phMuClick" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#d97706" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#d97706" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="day" fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
                        <YAxis fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <Area type="monotone" dataKey="Delivered" stroke="#2563eb" fill="url(#phMuDel)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="Opened" stroke="#059669" fill="url(#phMuOpen)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="Clicked" stroke="#d97706" fill="url(#phMuClick)" strokeWidth={2} dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{
                    position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    background: "radial-gradient(ellipse at center, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.7) 100%)",
                  }}>
                    <FaChartArea style={{ fontSize: 28, color: "#2563eb", marginBottom: 10, opacity: 0.7 }} />
                    <p style={{ fontSize: 14, fontWeight: 600, color: "#1f2937", margin: "0 0 4px", textAlign: "center", padding: "0 16px" }}>
                      Upload contacts with a preferred suburb to get started
                    </p>
                    <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px", textAlign: "center", padding: "0 24px", lineHeight: 1.5 }}>
                      They'll automatically receive suburb-level market insights. Email activity will appear here.
                    </p>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      style={{
                        background: "#2563eb", color: "#fff", border: "none",
                        borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 600,
                        cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                      }}
                    >
                      <FaCloudUploadAlt style={{ fontSize: 14 }} /> Upload Contacts
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Property Update Email Activity */}
            {loading ? (
              <ChartCard title="Property Update — Email Activity" isMobile={isMobile} height={isMobile ? 240 : 300}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                  <Spinner animation="border" />
                </div>
              </ChartCard>
            ) : puChart.length > 0 ? (
              <ChartCard title="Property Update — Email Activity" isMobile={isMobile} height={isMobile ? 240 : 300}>
                <EmailAreaChart data={puChart} idPrefix="pu" isMobile={isMobile} />
              </ChartCard>
            ) : (
              <div style={{
                background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)", overflow: "hidden",
              }}>
                <div style={{ padding: isMobile ? "20px 16px 16px" : "24px 28px 16px" }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", margin: 0 }}>Property Update — Email Activity</h3>
                </div>
                <div style={{ position: "relative", height: isMobile ? 200 : 260, padding: "0 12px" }}>
                  <div style={{ opacity: 0.35, filter: "blur(1px)", height: "100%", pointerEvents: "none" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={PLACEHOLDER_PU_CHART} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="phPuDel" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="phPuOpen" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="phPuClick" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#d97706" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#d97706" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="day" fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
                        <YAxis fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <Area type="monotone" dataKey="Delivered" stroke="#2563eb" fill="url(#phPuDel)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="Opened" stroke="#059669" fill="url(#phPuOpen)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="Clicked" stroke="#d97706" fill="url(#phPuClick)" strokeWidth={2} dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{
                    position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    background: "radial-gradient(ellipse at center, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.7) 100%)",
                  }}>
                    <FaHome style={{ fontSize: 28, color: "#d97706", marginBottom: 10, opacity: 0.7 }} />
                    <p style={{ fontSize: 14, fontWeight: 600, color: "#1f2937", margin: "0 0 4px", textAlign: "center", padding: "0 16px" }}>
                      Upload contacts with a property address to get started
                    </p>
                    <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px", textAlign: "center", padding: "0 24px", lineHeight: 1.5 }}>
                      They'll automatically receive personalised updates about their home. Email activity will appear here.
                    </p>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      style={{
                        background: "#d97706", color: "#fff", border: "none",
                        borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 600,
                        cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                      }}
                    >
                      <FaCloudUploadAlt style={{ fontSize: 14 }} /> Upload Contacts
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* SMS sent — grouped bar chart for Market & Property Update campaigns */}
            <ChartCard title="SMS Sent — Market & Property Updates" isMobile={isMobile} height={isMobile ? 240 : 300}>
              {loading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                  <Spinner animation="border" />
                </div>
              ) : smsChart.length === 0 ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#9ca3af", fontSize: 14, gap: 6 }}>
                  <FaSms style={{ fontSize: 22, color: "#9ca3af" }} />
                  No SMS sent in this period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={smsChart} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="day" tickFormatter={formatDay} fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
                    <YAxis fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "#6b7280" }} />
                    <Bar dataKey="Market Update" name="Market Update" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Property Update" name="Property Update" fill="#d97706" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Platform Events — stacked bar chart */}
            <ChartCard title="Platform Events" isMobile={isMobile} height={isMobile ? 260 : 320}>
              {loading ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                  <Spinner animation="border" />
                </div>
              ) : eventsChart.length === 0 ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#9ca3af", fontSize: 14 }}>
                  No event data for this period
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={eventsChart} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="day" tickFormatter={formatDay} fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} />
                    <YAxis fontSize={11} tick={{ fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: "#6b7280" }} />
                    {Object.entries(EVENT_LABELS).map(([key, label]) => (
                      <Bar key={key} dataKey={key} name={label} stackId="events" fill={EVENT_COLORS[key]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        )}
      </div>
    </div>
    <UploadContactsModal show={showUploadModal} onClose={() => setShowUploadModal(false)} />
    </>
  );
}